import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApiError, apiFetch, clearClientAuthRevoked } from './api';

type User = {
  id: string;
  email?: string;
  createdAt?: number;
  nickname?: string;
  avatarUrl?: string | null;
  status?: number;
  onlineTotalSeconds?: number;
  lastActiveAt?: number | null;
};

type AuthState = {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginByCode: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      for (let i = 0; i < 3; i++) {
        try {
          const res = await apiFetch<{ ok: boolean; user: User }>('/api/auth/me', { method: 'GET' });
          setUser(res?.user || null);
          return;
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) throw e;
          const retryable = !(e instanceof ApiError) || e.status === 502 || e.status === 503 || e.status === 504;
          if (!retryable || i === 2) throw e;
          await new Promise((r) => setTimeout(r, 600 + i * 600));
        }
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        const shouldTryRefresh = (() => {
          try {
            return localStorage.getItem('had_session_v1') === '1';
          } catch {
            return false;
          }
        })();
        if (!shouldTryRefresh) {
          setUser(null);
        } else {
          try {
            await apiFetch('/api/auth/refresh', { method: 'POST', body: JSON.stringify({}) });
            const res2 = await apiFetch<{ ok: boolean; user: User }>('/api/auth/me', { method: 'GET' });
            setUser(res2?.user || null);
          } catch {
            setUser(null);
          }
        }
      } else setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    clearClientAuthRevoked();
    await apiFetch('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
    try {
      localStorage.setItem('had_session_v1', '1');
    } catch {
    }
    await refresh();
  }, [refresh]);

  const loginByCode = useCallback(async (email: string, code: string) => {
    clearClientAuthRevoked();
    await apiFetch('/api/auth/login-code', { method: 'POST', body: JSON.stringify({ email, code }) });
    try {
      localStorage.setItem('had_session_v1', '1');
    } catch {
    }
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST', body: JSON.stringify({}) });
    } finally {
      try {
        localStorage.removeItem('had_session_v1');
      } catch {
      }
      clearClientAuthRevoked();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const id = window.setTimeout(() => {
      refresh();
    }, 600);
    return () => window.clearTimeout(id);
  }, [refresh]);

  const value = useMemo(() => ({ user, loading, refresh, login, loginByCode, logout }), [user, loading, refresh, login, loginByCode, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('AUTH_CONTEXT_MISSING');
  return ctx;
}
